/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { format } from 'date-fns'
import { groq } from 'next-sanity'
import ErrorPage from 'next/error'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import {
    FacebookIcon,
    FacebookShareButton,
    PinterestIcon,
    PinterestShareButton,
    TwitterIcon,
    TwitterShareButton
} from 'react-share'
import { CardProps } from 'src/common/components/Card'
import { Lane } from 'src/common/components/Lane'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { Tabs } from 'src/common/components/Tabs'
import {
    PortableText,
    urlFor,
    usePreviewSubscription
} from 'src/common/lib/sanity/sanity'
import { getClient } from 'src/common/lib/sanity/sanity.server'

const BASE_SHARE_URL = 'https://tw.web-dev.yes.fit/blog'

const postQuery = groq`
  *[_type == "post" && slug.current == $slug][0] {
	author->{
		name,
		"image": image.asset,
		"slug": slug.current,
		"biopt": pt::text(bio)
	},
	publishedAt,
    _id,
    title,
    body,
    mainImage,
    categories[]->{
      _id,
      title,
	  "slug": slug.current,
    },
    "slug": slug.current,
	"relatedPosts": *[_type=="post" && references(^.categories[]->_id)] | order(publishedAt desc){
		author->{
					name,
					"image": image.asset,
					"slug": slug.current,
			},
			publishedAt,
			title,
			"slug": slug.current,
			mainImage,
			categories[]->{
				title,
				"slug": slug.current,
			},
			"plaintext": pt::text(body),
	  }[1..15]
  }
`

// replace plaintext with pt::text() func once bug gets fixed in next-sanity

const Post = ({ data, preview }) => {
    const router = useRouter()

    const { data: post } = usePreviewSubscription(postQuery, {
        params: {
            slug: data?.post?.slug
        },
        initialData: data?.post,
        enabled: preview && data?.post?.slug
    })

    if (!post) return <ErrorPage statusCode={500} />

    const { title, mainImage, categories, body, relatedPosts } = post

    if (!router.isFallback && !data?.post?.slug) {
        return <ErrorPage statusCode={404} />
    }

    if (!data?.post) {
        return <ErrorPage statusCode={500} />
    }

    return (
        <AppLayout
            pageTitle={title}
            metaImage={urlFor(mainImage).size(1920, 500).quality(80).url()}
        >
            <div className="flex items-center justify-center relative">
                <Image
                    width={1920}
                    height={500}
                    src={mainImage.asset._ref}
                    loader={({ quality }) =>
                        urlFor(mainImage).size(1920, 500).quality(quality).url()
                    }
                    placeholder="blur"
                    blurDataURL={urlFor(mainImage).size(1920, 500).blur(99).url()}
                    className="w-full h-auto"
                />
            </div>

            <div className="max-w-screen-fullHd mx-auto ">
                <div className="bg-white ">
                    <Tabs
                        items={[
                            {
                                href: `/blog/${encodeURIComponent(post.slug)}`,
                                isActive: true,
                                text: 'Post'
                            }
                            // {
                            //     isActive: false,
                            //     text: 'Comments'
                            // }
                        ]}
                    />
                </div>
                <div className="flex content-between flex-col md:flex-row">
                    <div className="flex md:ml-8 py-3 md:py-5 pl-6 items-center flex-wrap">
                        {categories?.length ? (
                            <p className="font-medium mr-1 md:mr-5">Categories: </p>
                        ) : null}
                        {categories?.map((cat) => (
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                            <Link href={`/blog/categories/${cat.slug}`} key={cat._id}>
                                <a>
                                    <div className="py-1.5 px-4 rounded bg-yf-purple m-1">
                                        <p className="text-white font-medium text-xs md:text-sm uppercase">
                                            {cat.title ?? '...'}
                                        </p>
                                    </div>
                                </a>
                            </Link>
                        )) ?? []}
                    </div>
                    <div className="md:ml-auto flex py-1 md:py-5 pl-6 md:pl-0 items-center pr-5">
                        <div className="mr-2">
                            <FacebookShareButton
                                url={`https://yes.fit/${encodeURIComponent(post.slug)}`}
                            >
                                <FacebookIcon size={30} round={true} />
                            </FacebookShareButton>
                        </div>
                        <div className="mr-2">
                            <TwitterShareButton
                                url={`https://yes.fit/${encodeURIComponent(post.slug)}`}
                            >
                                <TwitterIcon size={30} round={true} />
                            </TwitterShareButton>
                        </div>
                        <div className="mr-2">
                            <PinterestShareButton
                                media={urlFor(mainImage).url()}
                                url={`${
                                    process.env.NEXT_PUBLIC_VERCEL_URL || BASE_SHARE_URL
                                }/${encodeURIComponent(post.slug)}`}
                            >
                                <PinterestIcon size={30} round={true} />
                            </PinterestShareButton>
                        </div>
                    </div>
                </div>

                <div className="h-px bg-gray-200 mb-10"></div>
            </div>

            <article className="prose max-w-7xl mx-auto px-5 mb-14">
                <PortableText blocks={body} />
            </article>

            <div className="max-w-7xl mx-auto px-5 mb-14">
                <p className="text-3xl font-medium mb-7">About the Author</p>
                <div className="flex">
                    <img
                        className="rounded-full border-solid border-2 border-yf-green mr-5 max-h-[100px] max-w-[100px]"
                        src={urlFor(post?.author?.image).size(100, 100).url()}
                    />
                    <div>
                        <Link href={`/trainers/${post.author?.slug}`}>
                            <a className="text-yf-green text-xl font-medium underline cursor-pointer block mb-2">
                                {post.author?.name}
                            </a>
                        </Link>
                        <div className="block">
                            <p>{post.author.biopt}</p>
                        </div>
                    </div>
                </div>
            </div>

            {relatedPosts?.length ? (
                <div className="max-w-7xl mx-auto mb-14">
                    <Lane
                        title="Related Posts"
                        data={
                            relatedPosts?.map((item): CardProps => {
                                return {
                                    cardType: 'blog',
                                    sanitySrc: item.mainImage,
                                    imageURL: '',
                                    title: item.title,
                                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                    href: `/blog/${item?.slug ?? ''}`,
                                    publishedDate: format(
                                        new Date(item.publishedAt),
                                        'MMM d'
                                    ),
                                    author: {
                                        imageURL: item?.author?.image
                                            ? urlFor(item?.author?.image)
                                                  .width(38)
                                                  .height(38)
                                                  .url()
                                            : null
                                    },
                                    category: item?.categories?.length
                                        ? {
                                              color: '#8C80F8',
                                              name: item.categories[0]?.title,
                                              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                                              href: `/blog/categories/${item.categories[0]?.slug}`
                                          }
                                        : null,
                                    previewText: item.plaintext.slice(0, 180)
                                }
                            }) ?? []
                        }
                    />
                </div>
            ) : null}
        </AppLayout>
    )
}

export async function getStaticProps({ params, preview = false }) {
    const post = await getClient(preview).fetch(postQuery, {
        slug: params.slug
    })

    return {
        props: {
            preview,
            data: { post }
        }
    }
}

export async function getStaticPaths() {
    const paths = await getClient(false).fetch(
        groq`*[_type == "post" && defined(slug.current)][].slug.current`
    )

    return {
        paths: paths.map((slug) => ({ params: { slug } })),
        fallback: true
    }
}

export default Post
